import { useEffect, useMemo, useState } from 'react';

import { Empty, Field } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { fmtDate, fullName, pct } from '../../lib/format.js';
import { bestExamAttempt } from '../../lib/progress.js';
import * as api from '../../lib/api.js';
import { useBackDismiss } from '../../hooks.js';
import { ExamDiagram, hasDiagram } from '../../components/ExamDiagram.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

/*
  Zaključni test — preverjanje znanja pred višinskimi leti.

  Nadomešča Google obrazec. Šola mora rezultat hraniti, zato je vsak
  oddan poskus trajen zapis: odgovori, izid, prag, ki je takrat veljal,
  in podpis.

  Ocenjevanje NI tukaj. Pravilni odgovori ostanejo v bazi; brskalnik
  tečajnika jih nikoli ne dobi. Klic `submit_exam` vrne samo izid.
*/

export function FinalTest({ profile, attempts, onChanged, readOnly = false }) {
  const { toast, confirm } = useUI();

  const [questions, setQuestions] = useState(null); // null = se nalaga
  const [threshold, setThreshold] = useState(80);
  const [answers, setAnswers] = useState({});
  const [podpis, setPodpis] = useState('');
  const [busy, setBusy] = useState(false);
  const [taking, setTaking] = useState(false);
  const [missing, setMissing] = useState([]);
  const [review, setReview] = useState(null); // { attempt, rows }
  const [loadingReview, setLoadingReview] = useState(false);

  const passed = useMemo(() => bestExamAttempt(attempts), [attempts]);
  const last = attempts?.[0] ?? null;

  // Sistemski gumb „nazaj" zapre reševanje ali pregled, ne aplikacije.
  useBackDismiss(taking, () => setTaking(false));
  useBackDismiss(!!review, () => setReview(null));

  useEffect(() => {
    if (!taking) return;
    let cancelled = false;
    Promise.all([api.loadExamQuestions(), api.loadExamThreshold()])
      .then(([q, t]) => {
        if (cancelled) return;
        setQuestions(q);
        setThreshold(t);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.bad(e.message ?? t('Vprašanj ni bilo mogoče naložiti.'));
          setTaking(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [taking, toast]);

  const answered = Object.keys(answers).length;
  const total = questions?.length ?? 0;

  const submit = async () => {
    const unanswered = (questions ?? []).filter((q) => answers[q.id] == null);
    if (unanswered.length) {
      setMissing(unanswered.map((q) => q.id));
      toast.bad(t('Odgovori še na {n} vprašanj.', { n: unanswered.length }));
      document.getElementById(`q-${unanswered[0].id}`)?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      return;
    }
    if (!podpis.trim()) {
      toast.bad(t('Vpiši svoje ime kot podpis.'));
      return;
    }

    const ok = await confirm({
      title: t('Oddam test?'),
      body: t('Po oddaji odgovorov ni več mogoče spremeniti.'),
      detail: [
        t('Odgovorjenih: {a} od {b}', { a: answered, b: total }),
        t('Za opravljeno je potrebnih {n} %', { n: threshold }),
      ],
      confirmLabel: t('Oddaj test'),
    });
    if (!ok) return;

    setBusy(true);
    try {
      const res = await api.submitExam(answers, podpis.trim());
      setTaking(false);
      setAnswers({});
      setPodpis('');
      if (res.passed) {
        toast.ok(t('Test opravljen — {s}.', { s: `${res.score}/${res.total}` }), { duration: 8000 });
      } else {
        toast.bad(
          t('Test ni opravljen: {s}. Poglej razlage in poskusi znova.', { s: `${res.score}/${res.total}` }),
          { duration: 12000 },
        );
      }
      // Pregled odpremo takoj: tu je vrednost testa — tečajnik vidi,
      // kaj je zgrešil, in prebere zakaj.
      await openReview(res);
      await onChanged();
    } catch (e) {
      toast.bad(e.message ?? t('Oddaja ni uspela.'));
    } finally {
      setBusy(false);
    }
  };

  const openReview = async (attempt) => {
    setLoadingReview(true);
    try {
      const rows = await api.loadExamReview(attempt.id);
      setReview({ attempt, rows });
    } catch (e) {
      toast.bad(e.message ?? t('Pregleda ni bilo mogoče naložiti.'));
    } finally {
      setLoadingReview(false);
    }
  };

  /* ── Pregled oddanega testa ──────────────────────────────── */
  if (review) {
    const wrong = review.rows.filter((r) => !r.is_correct);
    const a = review.attempt;

    return (
      <div className="stack stack--loose">
        <section className={`card card--pad ${a.passed ? 'card--ok' : ''}`}>
          <div className="row row--top">
            <Icon
              name={a.passed ? 'award' : 'alert'}
              size={26}
              className={a.passed ? 't-ok' : 't-bad'}
            />
            <div className="spacer">
              <h2 className="t-lg">{a.passed ? t('Test opravljen') : t('Test ni opravljen')}</h2>
              <p className="t-sm t-dim">
                {t('{s} pravilnih ({p} %). Za opravljeno je potrebnih {n} %.', {
                  s: `${a.score}/${a.total}`,
                  p: pct(a.score, a.total),
                  n: a.prag,
                })}
              </p>
            </div>
          </div>

          {wrong.length > 0 && (
            <p className="t-md" style={{ marginTop: 'var(--s-4)' }}>
              {t('Spodaj so vprašanja, ki si jih zgrešil, in razlaga za vsako. Preberi jih,')}
              {a.passed ? t(' da snov res sede.') : t(' nato pa test reši še enkrat.')}
            </p>
          )}

          <div className="row row--wrap" style={{ marginTop: 'var(--s-4)', gap: 'var(--s-2)' }}>
            {!a.passed && !readOnly && (
              <button
                className="btn btn--primary"
                onClick={() => {
                  setReview(null);
                  setTaking(true);
                }}
              >
                {t('Reši test znova')}
              </button>
            )}
            <button className="btn btn--secondary" onClick={() => setReview(null)}>
              <Icon name="left" size={15} /> {t('Nazaj')}
            </button>
          </div>
        </section>

        {wrong.length === 0 ? (
          <Empty
            icon="check"
            title={t('Vsa vprašanja pravilna')}
            hint={t('Ni česa popravljati.')}
          />
        ) : (
          <ol className="stack" style={{ listStyle: 'none' }}>
            {wrong.map((r) => (
              <li key={r.id} className="card card--pad stack stack--tight">
                <span className="t-md t-bold">
                  <span className="t-brand num">{r.ord}.</span> {r.question}
                </span>

                <span className="row t-sm t-bad" style={{ gap: 'var(--s-2)' }}>
                  <Icon name="x" size={15} />
                  <span>
                    {t('Tvoj odgovor:')} {r.chosen == null ? t('brez odgovora') : r.options[r.chosen]}
                  </span>
                </span>

                <span className="row t-sm t-ok" style={{ gap: 'var(--s-2)' }}>
                  <Icon name="check" size={15} />
                  <span>{t('Pravilno:')} {r.options[r.correct]}</span>
                </span>

                {r.razlaga && (
                  <p className="card card--brand card--pad t-md" style={{ marginTop: 'var(--s-1)' }}>
                    {r.razlaga}
                  </p>
                )}

                {/* Naložena fotografija prevlada nad vgrajeno shemo. */}
                {r.slika_url ? (
                  <img
                    src={r.slika_url}
                    alt={t('Ponazoritev: {q}', { q: r.question })}
                    loading="lazy"
                    style={{
                      width: '100%',
                      borderRadius: 'var(--r-md)',
                      border: '1px solid var(--border)',
                      marginTop: 'var(--s-1)',
                    }}
                  />
                ) : (
                  hasDiagram(r.ord) && <ExamDiagram ord={r.ord} title={r.question} />
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  }

  /* ── Pregled pred reševanjem ─────────────────────────────── */
  if (!taking) {
    return (
      <div className="stack stack--loose">
        <section className={`card card--pad ${passed ? 'card--ok' : 'card--brand'}`}>
          <div className="row row--top">
            <Icon name={passed ? 'award' : 'clipboard'} size={26} className={passed ? 't-ok' : 't-brand'} />
            <div className="spacer">
              <h2 className="t-lg">{t('Zaključni test')}</h2>
              <p className="t-sm t-dim">
                {t('Preverjanje znanja iz začetnega tečaja. Rešiti ga moraš')}{' '}
                <strong>{t('pred višinskimi leti')}</strong>.
              </p>
            </div>
          </div>

          {passed ? (
            <>
              <p className="row t-md t-ok" style={{ marginTop: 'var(--s-4)', gap: 'var(--s-2)' }}>
                <Icon name="check" size={17} /> {t('Opravljen')} {fmtDate(passed.submitted_at)} —{' '}
                {passed.score}/{passed.total} {t('točk')}.
              </p>
              {passed.score < passed.total && (
                <button
                  className="btn btn--secondary"
                  style={{ marginTop: 'var(--s-3)' }}
                  onClick={() => openReview(passed)}
                  disabled={loadingReview}
                >
                  {t('Poglej, kaj si zgrešil')}
                </button>
              )}
            </>
          ) : readOnly ? (
            <p className="t-sm t-dimmer" style={{ marginTop: 'var(--s-4)' }}>
              {t('Test še ni opravljen.')}
            </p>
          ) : (
            <button
              className="btn btn--primary btn--lg btn--block"
              style={{ marginTop: 'var(--s-4)' }}
              onClick={() => setTaking(true)}
            >
              {last ? t('Poskusi znova') : t('Začni test')}
            </button>
          )}
        </section>

        {last && !passed && (
          <section className="card card--pad stack stack--tight">
            <p className="t-md t-bad">
              {t('Zadnji poskus {d}: {s} — za opravljeno je bilo potrebnih {n} %.', {
                d: fmtDate(last.submitted_at),
                s: `${last.score}/${last.total}`,
                n: last.prag,
              })}
            </p>
            <p className="t-sm t-dim">
              {t('Poglej, katera vprašanja si zgrešil, in preberi razlage. Test lahko rešiš znova, kolikorkrat je treba.')}
            </p>
            {!readOnly && (
              <button
                className="btn btn--secondary"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => openReview(last)}
                disabled={loadingReview}
              >
                {loadingReview ? t('Nalagam …') : t('Poglej napake in razlage')}
              </button>
            )}
          </section>
        )}

        {attempts?.length > 1 && (
          <section className="card">
            <div className="card__head">
              <span className="card__title">{t('Vsi poskusi')}</span>
            </div>
            <div className="list">
              {attempts.map((a) => (
                <button key={a.id} className="list__row" onClick={() => openReview(a)}>
                  <span className="spacer t-md" style={{ textAlign: 'left' }}>
                    {fmtDate(a.submitted_at)}
                  </span>
                  <span className="t-sm num t-dim">
                    {a.score}/{a.total} · {pct(a.score, a.total)} %
                  </span>
                  <span className={`badge ${a.passed ? 'badge--ok' : 'badge--bad'}`}>
                    {a.passed ? t('Opravljen') : t('Ni opravljen')}
                  </span>
                  <Icon name="right" size={16} className="t-dimmer" />
                </button>
              ))}
            </div>
          </section>
        )}

        {!attempts?.length && (
          <Empty
            icon="clipboard"
            hint={t('Test je iz snovi začetnega tečaja. Vzemi si čas — odgovorov po oddaji ni več mogoče spremeniti.')}
          />
        )}
      </div>
    );
  }

  /* ── Reševanje ───────────────────────────────────────────── */
  if (questions === null) {
    return (
      <div className="card card--pad" aria-busy="true">
        <p className="t-md t-dim" role="status">
          {t('Nalagam vprašanja …')}
        </p>
      </div>
    );
  }

  return (
    <div className="stack">
      <section className="card card--brand card--pad row row--wrap">
        <div className="spacer">
          <h2 className="t-lg">{t('Zaključni test')}</h2>
          <p className="t-sm t-dim">
            {t('Za opravljeno je potrebnih {n} % pravilnih odgovorov.', { n: threshold })}
          </p>
        </div>
        <button className="btn btn--secondary btn--sm" onClick={() => setTaking(false)}>
          <Icon name="left" size={15} /> {t('Prekini')}
        </button>
      </section>

      <ol className="stack stack--loose" style={{ listStyle: 'none' }}>
        {questions.map((q, i) => {
          const isMissing = missing.includes(q.id) && answers[q.id] == null;
          return (
            <li
              key={q.id}
              id={`q-${q.id}`}
              className="card card--pad"
              style={isMissing ? { borderColor: 'var(--bad-text)' } : undefined}
            >
              <fieldset style={{ border: 0 }}>
                <legend className="t-md t-bold" style={{ marginBottom: 'var(--s-3)' }}>
                  <span className="t-brand num">{i + 1}.</span> {q.question}
                </legend>

                <div className="stack stack--tight">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[q.id] === oi;
                    return (
                      <label key={oi} className={`choice${chosen ? ' choice--on' : ''}`}>
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          className="checkbox"
                          style={{ borderRadius: '50%' }}
                          checked={chosen}
                          onChange={() => {
                            setAnswers((a) => ({ ...a, [q.id]: oi }));
                            setMissing((m) => m.filter((x) => x !== q.id));
                          }}
                        />
                        <span className="spacer t-md">{opt}</span>
                      </label>
                    );
                  })}
                </div>

                {isMissing && (
                  <p className="field__error" role="alert" style={{ marginTop: 'var(--s-2)' }}>
                    {t('Na to vprašanje še nisi odgovoril.')}
                  </p>
                )}
              </fieldset>
            </li>
          );
        })}
      </ol>

      <section className="card card--pad stack">
        <Field
          label={t('Podpis')}
          hint={t('Vpiši svoje ime in priimek.')}
          id="podpis"
        >
          <input
            id="podpis"
            className="input"
            value={podpis}
            placeholder={fullName(profile) ?? t('Ime in priimek')}
            autoComplete="name"
            onChange={(e) => setPodpis(e.target.value)}
          />
        </Field>
        <p className="t-sm t-dim">
          {t('S podpisom potrjujem, da razumem osnovna pravila letenja in da se bom izogibal manevrom, za katere nisem usposobljen.')}
        </p>
      </section>

      <div className="sticky-bar">
        <span className="spacer t-sm t-dim num">
          {t('Odgovorjenih {a} od {b}', { a: answered, b: total })}
        </span>
        <button className="btn btn--primary" onClick={submit} disabled={busy}>
          {busy ? t('Oddajam …') : t('Oddaj test')}
        </button>
      </div>
    </div>
  );
}
